import axios from "./http-service";
import { FEEDBACK_SUBMIT, FEEDBACK_GET } from "./endpoint-urls";

export type FeedbackType = "thumbs_up" | "thumbs_down";

export interface FeedbackSubmitRequest {
  message_id: string;
  feedback_type: FeedbackType;
}

export interface FeedbackSubmitResponse {
  success: boolean;
  message: string;
  is_update: boolean;
}

export interface FeedbackGetResponse {
  feedback_type: FeedbackType | null;
  created_at: string;
  updated_at: string;
}

// Submit or update feedback for a message
export const submitFeedback = async (
  messageId: string,
  feedbackType: FeedbackType,
  reason: string | null
): Promise<FeedbackSubmitResponse> => {
  try {
    const response = await axios.post(FEEDBACK_SUBMIT, {
      message_id: messageId,
      feedback_type: feedbackType,
      reason,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get existing feedback for a message
export const getFeedback = async (
  messageId: string
): Promise<FeedbackGetResponse> => {
  try {
    const response = await axios.get(FEEDBACK_GET(messageId));
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Batch get feedback for multiple messages
export const getBatchFeedback = async (
  messageIds: string[]
): Promise<Record<string, FeedbackType>> => {
  try {
    // Since there's no batch endpoint, we'll make individual requests
    // In production, you'd want to implement a batch endpoint
    const feedbackPromises = messageIds.map(async (messageId) => {
      try {
        const feedback = await getFeedback(messageId);
        return { messageId, feedbackType: feedback.feedback_type };
      } catch (error) {
        // If feedback doesn't exist, return null
        return { messageId, feedbackType: null };
      }
    });

    const results = await Promise.all(feedbackPromises);
    const feedbackMap: Record<string, FeedbackType> = {};

    results.forEach(({ messageId, feedbackType }) => {
      if (feedbackType) {
        feedbackMap[messageId] = feedbackType;
      }
    });

    return feedbackMap;
  } catch (error) {
    throw error;
  }
};